import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';

import { FormattedMessage } from '../../../util/reactIntl';
import { Modal, NamedLink } from '../../../components';
import * as log from '../../../util/log';

import css from './SaleAnnouncement.module.css';

// One-time announcement of seller sales, for sellers with a listing that's been up for 7 days
// (the soonest a listing can go on sale). Seen is saved on the account, so it shows once.
const SEEN_KEY = 'saleAnnouncementSeenAt';
const ELIGIBLE_AFTER_MS = 7 * 24 * 60 * 60 * 1000;
// Don't interrupt sign-in or a purchase
const PAGES_TO_SKIP = new Set(['CheckoutPage', 'AuthenticationPage', 'ConfirmPage']);
// The topbar remounts on every page; check eligibility once per visit
let checkedForUserId = null;

const localSeen = () => {
  try {
    return window.localStorage.getItem(SEEN_KEY) === '1';
  } catch (e) {
    return false;
  }
};

const rememberLocally = () => {
  try {
    window.localStorage.setItem(SEEN_KEY, '1');
  } catch (e) {
    // Private browsing: the account flag still covers it
  }
};

// Whether the signed-in seller has a published listing up for at least 7 days
const hasListingUpForAWeek = () => async (dispatch, getState, sdk) => {
  const query = { states: ['published'], perPage: 1, 'fields.listing': ['createdAt'] };
  const first = await sdk.ownListings.query({ ...query, page: 1 });
  const totalPages = first?.data?.meta?.totalPages || 0;
  if (totalPages === 0) {
    return false;
  }
  // Newest first: the last page holds the oldest published listing
  const last = totalPages > 1 ? await sdk.ownListings.query({ ...query, page: totalPages }) : first;
  const createdAt = last?.data?.data?.[0]?.attributes?.createdAt;
  return Boolean(createdAt) && Date.now() - new Date(createdAt).getTime() >= ELIGIBLE_AFTER_MS;
};

const markSeen = () => (dispatch, getState, sdk) =>
  sdk.currentUser.updateProfile({ privateData: { [SEEN_KEY]: new Date().toISOString() } });

const SaleAnnouncement = props => {
  const { currentUser, currentPage, isLoggedInAs, onManageDisableScrolling } = props;
  const dispatch = useDispatch();
  const [isOpen, setIsOpen] = useState(false);
  // Only mounted once it has been shown (most visitors never see it)
  const [wasShown, setWasShown] = useState(false);
  const userId = currentUser?.id?.uuid;
  const alreadySeen = !!currentUser?.attributes?.profile?.privateData?.[SEEN_KEY];

  useEffect(() => {
    if (
      !userId ||
      isLoggedInAs ||
      alreadySeen ||
      PAGES_TO_SKIP.has(currentPage) ||
      checkedForUserId === userId ||
      localSeen()
    ) {
      return;
    }
    checkedForUserId = userId;
    dispatch(hasListingUpForAWeek())
      .then(eligible => {
        if (eligible) {
          setWasShown(true);
          setIsOpen(true);
        }
      })
      .catch(e => log.error(e, 'sale-announcement-eligibility-failed'));
  }, [userId, isLoggedInAs, alreadySeen, currentPage, dispatch]);

  const close = () => {
    setIsOpen(false);
    rememberLocally();
    dispatch(markSeen()).catch(e => log.error(e, 'sale-announcement-mark-seen-failed'));
  };

  if (!wasShown || !onManageDisableScrolling) {
    return null;
  }

  return (
    <Modal
      id="SaleAnnouncement"
      isOpen={isOpen}
      onClose={close}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <div className={css.root}>
        <span className={css.badge}>
          <FormattedMessage id="SaleAnnouncement.badge" />
        </span>
        <h2 className={css.title}>
          <FormattedMessage id="SaleAnnouncement.title" />
        </h2>
        <p className={css.body}>
          <FormattedMessage id="SaleAnnouncement.body" />
        </p>
        <ul className={css.points}>
          <li>
            <FormattedMessage id="SaleAnnouncement.pointChoose" />
          </li>
          <li>
            <FormattedMessage id="SaleAnnouncement.pointBuyers" />
          </li>
          <li>
            <FormattedMessage id="SaleAnnouncement.pointEnds" />
          </li>
        </ul>
        <div className={css.actions}>
          <NamedLink name="ManageListingsPage" className={css.primaryButton} onClick={close}>
            <FormattedMessage id="SaleAnnouncement.cta" />
          </NamedLink>
          <button type="button" className={css.textButton} onClick={close}>
            <FormattedMessage id="SaleAnnouncement.later" />
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default SaleAnnouncement;
